'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ChevronRight,
  Zap,
  HelpCircle,
  Layers,
} from 'lucide-react';
import LearningTracks from '@/app/components/LearningTracks';

import { createClient } from '@/lib/supabase/client';


const TEACHING_STYLES = [
  {
    id: 'style-explainer',
    key: 'explainer',
    label: 'Explainer',
    icon: Zap,
    tagline: 'Clear and simple',
    description:
      "Breaks every concept into the clearest possible language. No jargon until you\'re ready for it.",
  },
  {
    id: 'style-challenger',
    key: 'challenger',
    label: 'Challenger',
    icon: HelpCircle,
    tagline: 'Asks questions first',
    description:
      'Leads with questions to surface what you already know, then fills the gaps with precision.',
  },
  {
    id: 'style-builder',
    key: 'builder',
    label: 'Builder',
    icon: Layers,
    tagline: 'Step-by-step logic',
    description:
      'Constructs understanding piece by piece — each idea is a foundation for the next.',
  },
];

type Level = 'beginner' | 'intermediate';
type StyleKey = 'explainer' | 'challenger' | 'builder';

export default function SubjectGrid() {
  const router = useRouter();
  const supabase = createClient();
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<Level>('beginner');
  const [selectedStyle, setSelectedStyle] = useState<StyleKey>('explainer');
  const [starting, setStarting] = useState(false);

  const handleStart = async () => {
    if (!selectedSubject) return;
    setStarting(true);

    // Gate: check if user is authenticated before starting a session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      router.push('/login?redirect=learning');
      setStarting(false);
      return;
    }

    setTimeout(() => {
      router.push(
        `/learning-session?subject=${selectedSubject}&level=${selectedLevel}&style=${selectedStyle}`
      );
    }, 400);
  };

  return (
    <section className="px-6 lg:px-10 xl:px-16 pb-20 max-w-screen-2xl mx-auto">
      {/* Step 1: Choose Subject via Learning Tracks */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0">
            1
          </span>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Choose a subject</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Select one of the subjects below — each has full session content ready to start
            </p>
          </div>
        </div>
        <LearningTracks
          selectedSubject={selectedSubject}
          onSelectSubject={setSelectedSubject}
        />
      </div>

      {/* Step 2: Choose Level */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0">
            2
          </span>
          <h2 className="text-lg font-semibold text-foreground">Select your level</h2>
        </div>
        <div className="flex gap-3">
          {(['beginner', 'intermediate'] as Level[]).map((level) => (
            <button
              key={`level-${level}`}
              onClick={() => setSelectedLevel(level)}
              className={`px-5 py-3 rounded-xl border-2 text-sm font-semibold transition-all duration-150 capitalize ${
                selectedLevel === level
                  ? 'border-primary bg-primary/5 text-primary' :'border-border bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground ml-1">
          {selectedLevel === 'beginner' ?'No prior knowledge needed. We start from first principles.' :'You have some background. We build on what you know.'}
        </p>
      </div>

      {/* Step 3: Choose Teaching Style */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-5">
          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0">
            3
          </span>
          <h2 className="text-lg font-semibold text-foreground">Choose how you want to be taught</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TEACHING_STYLES.map((style) => {
            const StyleIcon = style.icon;
            const isSelected = selectedStyle === style.key;
            return (
              <button
                key={style.id}
                onClick={() => setSelectedStyle(style.key as StyleKey)}
                className={`text-left p-5 rounded-xl border-2 bg-card transition-all duration-200 ${
                  isSelected
                    ? 'border-accent ring-2 ring-accent/10 bg-accent/5' :'border-border hover:border-accent/30 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <StyleIcon size={16} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">{style.label}</div>
                    <div className="text-xs text-muted-foreground">{style.tagline}</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{style.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleStart}
          disabled={!selectedSubject || starting}
          className={`flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold transition-all duration-150 active:scale-95 ${
            selectedSubject && !starting
              ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          {starting ? (
            <>
              <svg
                className="animate-spin"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="28"
                  strokeDashoffset="10"
                />
              </svg>
              Starting session…
            </>
          ) : (
            <>
              Start Learning
              <ChevronRight size={16} />
            </>
          )}
        </button>
        {!selectedSubject && (
          <p className="text-xs text-muted-foreground">Select a subject above to continue</p>
        )}
      </div>
    </section>
  );
}